import dynamic from 'next/dynamic';
import React, { Fragment } from 'react';

const LoadingAnimation = dynamic(() => import('ui/components/atoms/LoadingAnimation'), {
  ssr: false,
});

const sizeClassName = {
    small: 'h-[38px] !text-[12px] rounded',
    large: 'h-[52px] text-base leading-[22px] rounded',
  },
  iconClassname = {
    fill: '',
    text: 'text',
    icon: 'rounded-full',
  },
  sizeIconClassName = {
    small: 'h-8 w-8',
    medium: 'h-12 w-12',
    large: 'h-14 w-14',
  };

interface ButtonProps {
  children?: React.ReactNode;
  iconType?: 'fill' | 'outline' | 'text' | 'icon';
  size?: 'small' | 'medium' | 'large';
  variant?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  block?: boolean;
  className?: string;
  style?: React.CSSProperties;
  isLoading?: boolean;
  pressed?: boolean;
  type?: string;
  buttonType?: 'submit' | 'reset' | 'button';
  textClassName?: string;
  buttonID?: string;
  textStyle?: React.CSSProperties;
  buttonRef?: React.MutableRefObject<HTMLButtonElement>;
  loadingStyle?: React.CSSProperties;
  dataTestId?: string;
}

const Button = ({
  children,
  pressed = false,
  type = 'submit',
  buttonType = 'submit',
  textClassName = '',
  disabled = false,
  block = false,
  variant = 'bg-supportiveRed',
  size = 'large',
  iconType = 'fill',
  isLoading = false,
  className = '',
  style = {},
  textStyle = {},
  buttonID = '',
  buttonRef,
  loadingStyle,
  dataTestId,
  ...props
}: ButtonProps & Omit<React.ComponentProps<'button'>, 'type'>) => {
  const classNameAssigned = [
    'relative flex whitespace-nowrap',
    'justify-center items-center px-4 gap-x-1.5 font-semibold',
    disabled || isLoading ? 'bg-reliableBlack20' : variant,
    iconClassname[iconType],
    type === 'icon' ? sizeIconClassName[size] : sizeClassName[size],
    block ? 'w-full' : '',
    pressed ? 'pressed' : '',
    isLoading ? 'bg-reliableBlack20 cursor-default' : '',
    className,
  ];

  return (
    <button
      id={buttonID}
      className={classNameAssigned.join(' ')}
      disabled={isLoading || disabled}
      style={style}
      type={buttonType}
      data-testid={dataTestId}
      ref={buttonRef}
      {...props}
    >
      {children ? (
        <div className={`text-center flex ${textClassName}`} style={textStyle}>
          {!isLoading ? children : <LoadingAnimation style={loadingStyle} />}
        </div>
      ) : (
        <Fragment />
      )}
    </button>
  );
};

export default Button;
